// Merchant console. KYB pill in the top-right, 4 stat tiles, quote
// inbox + active fulfillments + payout preview.
//
// Demo viewer: Marché Demo Léogâne (status=approved, KYB skipped).
// A real merchant who registered through /register?role=merchant would
// see status=applied/kyb_review and a banner blocking the inbox until
// approved — that branch lands in Phase 2 with the KYB workflow.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox, Truck, Wallet, TrendingUp, LayoutDashboard, Package,
  ShoppingBag, Pill, MapPin, Clock, CheckCircle2, Settings,
  Banknote, FileText, ShieldCheck, BadgeCheck, Download, Loader2, Store,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import {
  Card, SectionHeader, StatTile, ActivityRow, StatusPill, DemoDataBadge,
} from '../components/shared/primitives';
import { certificates, type CertLang } from '../lib/api';

const MERCHANT_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',      icon: LayoutDashboard, href: '/merchant' },
    { id: 'inbox',      label: 'Quote requests', icon: Inbox,           href: '/merchant/quote-requests', badge: 3 },
    { id: 'orders',     label: 'Active orders',  icon: Package,         href: '/merchant/orders', badge: 2 },
  ]},
  { title: 'Payouts', items: [
    { id: 'payouts',    label: 'Payouts',        icon: Banknote,        href: '/merchant/payouts' },
  ]},
  { title: 'Account', items: [
    { id: 'profile',    label: 'Business profile', icon: Store,         href: '/merchant/settings/profile' },
    { id: 'settings',   label: 'Settings',       icon: Settings,        href: '/merchant/settings' },
  ]},
];

interface MerchantProfile { business_name?: string; status?: string; merchant_code?: string; city?: { name: string } }

function MerchantCertCard({ code }: { code?: string }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await certificates.mine(lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 shrink-0"><BadgeCheck className="w-5 h-5" /></span>
        <div>
          <p className="text-[13.5px] font-semibold text-slate-900">Approved merchant</p>
          <p className="text-[12px] text-slate-500">Merchant ID <span className="font-mono text-slate-900">{code ?? '—'}</span></p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5 bg-white" aria-label="Certificate language">
          <option value="en">English</option><option value="fr">Français</option><option value="ht">Kreyòl</option>
        </select>
        <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12.5px] font-semibold hover:bg-brand-700 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Certificate
        </button>
      </div>
    </Card>
  );
}

export function MerchantDashboard() {
  const { user } = useAuth();
  const mp = (user?.merchant_profile ?? {}) as MerchantProfile;
  const status = mp.status ?? 'applied';
  const approved = status === 'approved';

  return (
    <PortalShell
      nav={MERCHANT_NAV}
      activeId="dashboard"
      title={mp.business_name ?? 'Merchant'}
      subtitle="Quote requests, active orders, and payouts."
      actions={
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-50 text-[11px] font-semibold text-slate-700">
          <ShieldCheck className={`w-3.5 h-3.5 ${approved ? 'text-emerald-600' : 'text-amber-600'}`} />
          KYB <StatusPill status={status} />
        </span>
      }
    >
      <div className="space-y-6">
        {!approved && (
          <Card className="border-amber-200 bg-amber-50">
            <p className="text-[13px] font-semibold text-amber-900">Application under review</p>
            <p className="mt-1 text-[12px] text-amber-800">
              Your KYB documents are being reviewed. You'll be able to receive quote requests once approved (typically 1–3 business days).
            </p>
          </Card>
        )}

        {approved && <MerchantCertCard code={mp.merchant_code} />}

        <div className="flex items-center gap-2 -mb-2"><DemoDataBadge note="real numbers ship with Phase 2 (quotes + Stripe)" /></div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile icon={Inbox}      tone="brand"   label="New quote requests" value="3"  hint="In your zone + categories" trend={{ value: '+2 today', positive: true }} />
          <StatTile icon={Truck}      tone="amber"   label="Active orders"      value="2"  hint="Preparing or out for delivery" />
          <StatTile icon={Wallet}     tone="default" label="Pending payout"     value="HTG 9,840" hint="Ready to batch · 4 orders" />
          <StatTile icon={TrendingUp} tone="emerald" label="This week"          value="HTG 22,150" hint="Revenue after fees" trend={{ value: '+18% vs last', positive: true }} />
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Quote inbox */}
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <SectionHeader
                title="Quote requests"
                subtitle="Customers waiting for your price. Quotes expire after 24h."
                actions={<Link to="/merchant/quote-requests" className="text-[12px] font-semibold text-brand-600 hover:underline">View all →</Link>}
              />
              <div className="divide-y divide-slate-100 -mx-1 px-1">
                <InboxRow
                  code="REQ-1042" category="Groceries" icon={ShoppingBag}
                  city="Léogâne, Centre"
                  detail="Rice 5lb, Cooking oil, Sugar 2lb, Eggs 12, Beans 2lb"
                  age="20 min ago"
                />
                <InboxRow
                  code="REQ-1041" category="Pharmacy" icon={Pill}
                  city="Léogâne, Centre"
                  detail="Lisinopril 20mg #30, Acetaminophen 500mg"
                  age="1h ago"
                />
                <InboxRow
                  code="REQ-1039" category="Pharmacy" icon={Pill}
                  city="Léogâne, Nord"
                  detail="Amoxicillin 500mg #21, ORS sachets x4"
                  age="3h ago"
                />
              </div>
            </Card>

            <Card>
              <SectionHeader
                title="Active orders"
                subtitle="Paid orders to prepare and deliver."
                actions={<Link to="/merchant/orders" className="text-[12px] font-semibold text-brand-600 hover:underline">View all →</Link>}
              />
              <table className="text-[12.5px] w-full -mt-1 block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left py-2 font-medium">Order</th>
                    <th className="text-left py-2 font-medium">Recipient</th>
                    <th className="text-left py-2 font-medium">Status</th>
                    <th className="text-right py-2 font-medium">HTG</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="py-2.5"><span className="font-mono text-cyan-700">REQ-1037</span> · Groceries</td>
                    <td className="py-2.5">Maria, Centre</td>
                    <td className="py-2.5"><StatusPill status="ready" /></td>
                    <td className="py-2.5 text-right font-mono">2,250</td>
                  </tr>
                  <tr>
                    <td className="py-2.5"><span className="font-mono text-cyan-700">REQ-1024</span> · Pharmacy</td>
                    <td className="py-2.5">Maria, Centre</td>
                    <td className="py-2.5"><StatusPill status="preparing" /></td>
                    <td className="py-2.5 text-right font-mono">1,820</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </div>

          {/* Right column: payouts + activity */}
          <div className="space-y-5">
            <Card>
              <SectionHeader
                title="Payouts"
                actions={<Link to="/merchant/payouts" className="text-[12px] font-semibold text-brand-600 hover:underline">All →</Link>}
              />
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Next payout</p>
                    <p className="mt-0.5 text-[18px] font-semibold text-slate-900 font-mono">HTG 9,840</p>
                    <p className="text-[11px] text-slate-500">4 confirmed orders</p>
                  </div>
                  <Banknote className="w-7 h-7 text-emerald-500" />
                </div>
                <PayoutHistoryRow date="Jun 3" amount="HTG 14,200" method="MonCash" status="paid" />
                <PayoutHistoryRow date="May 27" amount="HTG 11,800" method="MonCash" status="paid" />
                <PayoutHistoryRow date="May 20" amount="HTG 8,600" method="Bank" status="paid" />
              </div>
            </Card>

            <Card>
              <SectionHeader title="Activity" />
              <div className="divide-y divide-slate-100 -mx-1 px-1">
                <ActivityRow
                  icon={CheckCircle2} iconTone="emerald"
                  title={<>Maria confirmed delivery of <strong>REQ-1037</strong></>}
                  meta="PIN entered · payout queued"
                  time="2h ago"
                />
                <ActivityRow
                  icon={Truck} iconTone="amber"
                  title={<><strong>REQ-1024</strong> marked out for delivery</>}
                  time="4h ago"
                />
                <ActivityRow
                  icon={FileText} iconTone="brand"
                  title={<>Quote sent for <strong>REQ-1042</strong></>}
                  meta="HTG 2,070 + 250 delivery"
                  time="5h ago"
                />
              </div>
            </Card>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 inline-flex items-center gap-1">
          <FileText className="w-3 h-3" /> All numbers above are demo placeholders. Real data populates when Quote + Fulfillment controllers land in Phase 2.
        </p>
      </div>
    </PortalShell>
  );
}

function InboxRow({
  code, category, icon: Icon, city, detail, age,
}: {
  code: string; category: string; icon: React.ComponentType<{ className?: string }>;
  city: string; detail: string; age: string;
}) {
  return (
    <div className="py-3 flex items-start gap-3">
      <span className="inline-flex w-10 h-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[12px] text-cyan-700">{code}</span>
          <span className="text-[13px] font-semibold text-slate-900">· {category}</span>
        </div>
        <p className="text-[11.5px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {city}
          <Clock className="w-3 h-3 ml-2" /> {age}
        </p>
        <p className="text-[12px] text-slate-700 mt-1 truncate">{detail}</p>
      </div>
      <Link to="/merchant/quote-requests" className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-md bg-brand-600 text-white text-[11.5px] font-semibold hover:bg-brand-700">
        Build quote
      </Link>
    </div>
  );
}

function PayoutHistoryRow({ date, amount, method, status }: { date: string; amount: string; method: string; status: string }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <div>
        <p className="text-slate-900 font-medium">{date}</p>
        <p className="text-[11px] text-slate-500">{method}</p>
      </div>
      <div className="text-right">
        <p className="font-mono text-slate-900">{amount}</p>
        <StatusPill status={status} />
      </div>
    </div>
  );
}
